import { SectionHeader } from '@/components/ui';
import type { SaleHistoryEntry } from '@/seo/brandHubs';

export interface BrandFaqSectionProps {
  brandName: string;
  /** Publicly visible stores in the page's country. */
  storeCount: number;
  /** Sale history rows — length determines which questions appear. */
  saleHistory: readonly SaleHistoryEntry[];
  className?: string;
}

/**
 * FAQ section for the brand hub (web plan W3.6).
 *
 * Plain HTML `<dl>` — NO FAQPage JSON-LD markup (plan says "no FAQPage
 * markup"). Questions appear based on available data.
 */
export function BrandFaqSection({
  brandName,
  storeCount,
  saleHistory,
  className,
}: BrandFaqSectionProps) {
  return (
    <section aria-label={`${brandName} FAQs`} className={className}>
      <SectionHeader title={`${brandName} FAQ`} />
      <dl className="mt-4 space-y-4">
        {/* Stores question */}
        <div>
          <dt className="font-semibold text-ink">
            Where can I find {brandName} stores?
          </dt>
          <dd className="mt-1 text-sm text-ink-secondary">
            {storeCount > 0
              ? `${brandName} has ${storeCount} store${storeCount === 1 ? '' : 's'} listed on PromoStork. Use the store list above to find locations near you.`
              : `Store locations for ${brandName} are not listed yet. Check the brand's website for the latest locations.`}
          </dd>
        </div>

        {/* Sales question */}
        <div>
          <dt className="font-semibold text-ink">
            Does {brandName} have sales?
          </dt>
          <dd className="mt-1 text-sm text-ink-secondary">
            {saleHistory.length > 0
              ? `Yes — ${brandName} has run ${saleHistory.length} sale${saleHistory.length === 1 ? '' : 's'} tracked on PromoStork. You can see the full history in the sale history table above.`
              : `PromoStork has not tracked any past sales from ${brandName} yet. Follow ${brandName} to get notified when a sale is announced.`}
          </dd>
        </div>
      </dl>
    </section>
  );
}

export default BrandFaqSection;
